import React, { useEffect, useMemo, useState } from "react";
import { Spin, Tag, Tooltip, Modal, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { fetchProjectActivity } from "../../../../core/data/redux/projects/thunks";
import * as projectsApi from "../../../../core/data/redux/services/projectsApi";
import {
  selectActivity,
  selectActivityLoading,
  selectCurrentProject,
  selectCurrentProjectLoading,
  selectMembers,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import {
  PROJECT_PRIORITY_OPTIONS,
  STATUS_CATEGORY_COLORS,
} from "../../constants/projects.constants";
import UserAvatar from "../../components/UserAvatar";
import { all_routes } from "../../../router/all_routes";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

import type { AppDispatch } from "../../../../core/data/redux/store";


const ACTION_ICONS: Record<string, { icon: string; color: string; bg: string }> = {
  created: { icon: "ti ti-plus", color: "#10b981", bg: "#ecfdf5" },
  updated: { icon: "ti ti-edit", color: "#3b82f6", bg: "#eff6ff" },
  deleted: { icon: "ti ti-trash", color: "#ef4444", bg: "#fef2f2" },
  moved: { icon: "ti ti-arrows-move", color: "#f59e0b", bg: "#fffbeb" },
  commented: { icon: "ti ti-message", color: "#8b5cf6", bg: "#faf5ff" },
  started_sprint: { icon: "ti ti-player-play", color: "#3b82f6", bg: "#eff6ff" },
  completed_sprint: { icon: "ti ti-flag-check", color: "#10b981", bg: "#ecfdf5" },
  added: { icon: "ti ti-user-plus", color: "#3b82f6", bg: "#eff6ff" },
  removed: { icon: "ti ti-user-minus", color: "#ef4444", bg: "#fef2f2" },
  role_changed: { icon: "ti ti-user-edit", color: "#f59e0b", bg: "#fffbeb" },
};

const cardStyle: React.CSSProperties = {
  borderRadius: 14,
  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
  border: "none",
};

const ProjectOverview: React.FC = () => {
  const { projectKey } = useParams<{ projectKey: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const [modalVisible, setModalVisible] = useState(false);
  const [modalActivities, setModalActivities] = useState<any[]>([]);
  const [modalPage, setModalPage] = useState(1);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalHasMore, setModalHasMore] = useState(true);

  const project = useSelector(selectCurrentProject);
  const projectLoading = useSelector(selectCurrentProjectLoading);
  const statuses = useSelector(selectStatuses);
  const members = useSelector(selectMembers);
  const activity = useSelector(selectActivity);
  const activityLoading = useSelector(selectActivityLoading);

  useEffect(() => {
    if (project?.id) dispatch(fetchProjectActivity({ projectId: project.id }));
  }, [dispatch, project?.id]);

  const loadModalActivities = async (page: number, append = false) => {
    if (!project || !project.company_id) return;
    setModalLoading(true);
    try {
      const data = await projectsApi.listProjectActivity(
        String(project.company_id),
        project.company_type,
        project.id,
        page,
        20
      );
      const fetched = data?.activity || [];
      if (append) {
        setModalActivities((prev) => [...prev, ...fetched]);
      } else {
        setModalActivities(fetched);
      }
      setModalHasMore(data?.pagination?.page < data?.pagination?.total_pages);
      setModalPage(page);
    } catch (error) {
      console.error("Failed to load activities", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleOpenModal = () => {
    setModalVisible(true);
    loadModalActivities(1, false);
  };

  const priorityLabel = useMemo(
    () =>
      PROJECT_PRIORITY_OPTIONS.find((o) => o.value === project?.priority)?.label ||
      "—",
    [project?.priority]
  );

  const creatorName = useMemo(() => {
    if (!project) return "—";
    const member = members.find((m: any) => Number(m.user_id) === Number(project.created_by));
    return member?.user_snapshot?.name || (project as any).creator_snapshot?.name || project.created_by || "—";
  }, [project, members]);

  if (projectLoading && !project) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-white"
        style={{ minHeight: 400, borderRadius: 14 }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "2.5rem", height: "2.5rem" }}
          />
          <p className="mt-3 text-muted" style={{ fontSize: 13.5 }}>Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-white"
        style={{ minHeight: 300, borderRadius: 14, border: "1px solid #e2e8f0" }}
      >
        <div className="text-center">
          <i className="ti ti-folder-off" style={{ fontSize: 48, color: "#cbd5e1" }} />
          <h5 className="mt-3 mb-2 fw-semibold" style={{ color: "#334155" }}>
            Project Not Found
          </h5>
          <p className="text-muted mb-0">
            Project <strong>{projectKey}</strong> could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-lg-8">
        {/* About */}
        <div className="card mb-3" style={cardStyle}>
          <div className="card-body bg-white rounded-3">
            <h6 className="mb-3 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
              <i className="ti ti-info-circle" style={{ fontSize: 18, color: "#3b82f6" }} />
              About this project
            </h6>
            {project.description ? (
              <div
                className="text-body"
                style={{
                  padding: "12px 14px",
                  background: "#f8fafc",
                  borderRadius: 10,
                  fontSize: 13.5,
                  lineHeight: 1.7,
                }}
                dangerouslySetInnerHTML={{ __html: project.description }}
              />
            ) : (
              <div
                className="text-muted text-center"
                style={{
                  padding: "20px 14px",
                  background: "#f8fafc",
                  borderRadius: 10,
                  fontSize: 13,
                }}
              >
                <i className="ti ti-notes-off d-block mb-2" style={{ fontSize: 24, color: "#cbd5e1" }} />
                No description provided.
              </div>
            )}
            <div className="d-flex flex-wrap gap-2 mt-3">
              <InfoChip icon="ti ti-flag" label="Priority" value={priorityLabel} bg="#eff6ff" color="#2563eb" />
              <InfoChip icon="ti ti-calendar" label="Start" value={project.start_date ? humanReadableDate(project.start_date) : "—"} bg="#f1f5f9" color="#475569" />
              <InfoChip icon="ti ti-calendar-due" label="End" value={project.end_date ? humanReadableDate(project.end_date) : "—"} bg="#faf5ff" color="#7c3aed" />
              <InfoChip icon="ti ti-user" label="Created by" value={String(creatorName)} bg="#ecfdf5" color="#059669" />
            </div>
          </div>
        </div>

        {/* Activity */}
        <div className="card" style={cardStyle}>
          <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
            <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
              <i className="ti ti-history" style={{ fontSize: 18, color: "#8b5cf6" }} />
              Recent Activity
            </h6>
            <button
              onClick={handleOpenModal}
              className="btn btn-sm d-flex align-items-center gap-1"
              style={{
                borderRadius: 8,
                background: "#f1f5f9",
                color: "#475569",
                fontSize: 12,
                fontWeight: 500,
                border: "none",
              }}
            >
              <i className="ti ti-list" style={{ fontSize: 14 }} />
              View All
            </button>
          </div>
          <div className="card-body bg-white rounded-3">
            {activityLoading ? (
              <div className="text-center py-3">
                <Spin />
              </div>
            ) : activity.length === 0 ? (
              <div className="text-center py-4">
                <i className="ti ti-history-off" style={{ fontSize: 36, color: "#cbd5e1" }} />
                <p className="text-muted mb-0 mt-2" style={{ fontSize: 13 }}>No activity yet.</p>
              </div>
            ) : (
              <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                {activity.slice(0, 10).map((a) => {
                  const actionMeta = ACTION_ICONS[a.action] || {
                    icon: "ti ti-history",
                    color: "#64748b",
                    bg: "#f1f5f9",
                  };
                  return (
                    <li
                      key={a.id}
                      className="d-flex gap-2"
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f8fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 32,
                          height: 32,
                          flexShrink: 0,
                          background: actionMeta.bg,
                        }}
                      >
                        <i className={actionMeta.icon} style={{ color: actionMeta.color, fontSize: 14 }} />
                      </div>
                      <div className="flex-grow-1 min-width-0">
                        <div style={{ fontSize: 13 }}>
                          <strong style={{ color: "#1e293b" }}>
                            {a.actor_snapshot?.name || `User #${a.actor_id}`}
                          </strong>{" "}
                          <span className="text-muted">{a.action.replace(/_/g, " ")}</span>{" "}
                          <span style={{ color: "#475569", fontWeight: 500 }}>
                            {a.entity_type.toLowerCase()}
                          </span>
                        </div>
                        <Tooltip title={new Date(a.created_at).toLocaleString()}>
                          <span className="text-muted" style={{ fontSize: 11.5 }}>
                            {humanReadableDate(a.created_at)}
                          </span>
                        </Tooltip>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="col-lg-4">
        {/* Workflow */}
        <div className="card mb-3" style={cardStyle}>
          <div className="card-header pb-1 border-0 d-flex align-items-center justify-content-between">
            <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
              <i className="ti ti-git-branch" style={{ fontSize: 18, color: "#10b981" }} />
              Workflow
            </h6>
            <Link
              to={all_routes.projectSettingsBoard.replace(
                ":projectKey",
                project.key
              )}
              className="btn btn-sm d-flex align-items-center gap-1"
              style={{
                borderRadius: 8,
                background: "#f1f5f9",
                color: "#475569",
                fontSize: 12,
                fontWeight: 500,
                border: "none",
              }}
            >
              <i className="ti ti-settings" style={{ fontSize: 14 }} />
              Configure
            </Link>
          </div>
          <div className="card-body bg-white rounded-3">
            {statuses.length === 0 ? (
              <div className="text-center py-3">
                <i className="ti ti-columns" style={{ fontSize: 32, color: "#cbd5e1" }} />
                <p className="text-muted small mb-0 mt-2">
                  No columns configured yet.
                </p>
              </div>
            ) : (
              <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
                {statuses.map((s) => (
                  <li
                    key={s.id}
                    className="d-flex align-items-center gap-2"
                    style={{
                      padding: "7px 10px",
                      borderRadius: 8,
                      background: "#f8fafc",
                      fontSize: 13,
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f1f5f9";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#f8fafc";
                    }}
                  >
                    <span
                      className="d-inline-block rounded-circle"
                      style={{
                        width: 10,
                        height: 10,
                        background:
                          s.color || STATUS_CATEGORY_COLORS[s.category],
                        flexShrink: 0,
                      }}
                    />
                    <span className="flex-grow-1" style={{ fontWeight: 500, color: "#1e293b" }}>
                      {s.name}
                    </span>
                    <span
                      className="text-uppercase"
                      style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}
                    >
                      {s.category.replace("_", " ")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Members */}
        <div className="card" style={cardStyle}>
          <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
            <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
              <i className="ti ti-users-group" style={{ fontSize: 18, color: "#f59e0b" }} />
              Members ({members.length})
            </h6>
            <Link
              to={all_routes.projectSettingsMembers.replace(
                ":projectKey",
                project.key
              )}
              className="btn btn-sm d-flex align-items-center gap-1"
              style={{
                borderRadius: 8,
                background: "#f1f5f9",
                color: "#475569",
                fontSize: 12,
                fontWeight: 500,
                border: "none",
              }}
            >
              <i className="ti ti-settings" style={{ fontSize: 14 }} />
              Manage
            </Link>
          </div>
          <div className="card-body bg-white rounded-3">
            {members.length === 0 ? (
              <div className="text-center py-3">
                <i className="ti ti-user-off" style={{ fontSize: 32, color: "#cbd5e1" }} />
                <p className="text-muted small mb-0 mt-2">No members yet.</p>
              </div>
            ) : (
              <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
                {members.slice(0, 8).map((m) => (
                  <li
                    key={m.id}
                    className="d-flex align-items-center gap-2"
                    style={{
                      padding: "7px 10px",
                      borderRadius: 8,
                      background: "#f8fafc",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                  >
                    <UserAvatar
                      name={m.user_snapshot?.name}
                      img={m.user_snapshot?.img}
                      size={28}
                      fontSize={11}
                    />

                    <div className="flex-grow-1" style={{ minWidth: 0, overflow: "hidden" }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "#1e293b" }}>
                        {m.user_snapshot?.name || `User #${m.user_id}`}
                      </div>
                      {m.user_snapshot?.email && (
                        <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
                          {m.user_snapshot.email}
                        </div>
                      )}
                    </div>

                    <Tag
                      style={{
                        margin: 0,
                        fontSize: 10,
                        borderRadius: 6,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {m.role}
                    </Tag>
                  </li>
                ))}
                {members.length > 8 && (
                  <li
                    className="text-center"
                    style={{ padding: "6px", fontSize: 12, color: "#64748b" }}
                  >
                    +{members.length - 8} more members
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>

      <Modal
        title={
          <div className="d-flex align-items-center gap-2">
            <i className="ti ti-history" style={{ color: "#8b5cf6", fontSize: 20 }} />
            <span>All Project Activity</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
        centered
        width="640px"
        zIndex={1001}
        styles={{
          content: { padding: 0, borderRadius: 12, overflow: "hidden" },
          header: { padding: "14px 20px 12px", borderBottom: "1px solid #f0f0f0", marginBottom: 0 },
          footer: { padding: "10px 20px 14px", borderTop: "1px solid #f0f0f0", marginTop: 0 },
          mask: {
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            background: "rgba(0,0,0,0.45)",
          },
        }}
      >
        <div className="d-flex flex-column gap-3" style={{
          maxHeight: "65vh",
          overflowY: "auto",
          padding: "16px 24px 20px",
          overflowX: "hidden",
        }}>
          {modalActivities.length === 0 && !modalLoading ? (
            <div className="text-center py-4">
              <i className="ti ti-history-off" style={{ fontSize: 36, color: "#cbd5e1" }} />
              <p className="text-muted mb-0 mt-2" style={{ fontSize: 13 }}>No activity found.</p>
            </div>
          ) : (
            <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
              {modalActivities.map((a) => {
                const actionMeta = ACTION_ICONS[a.action] || {
                  icon: "ti ti-history",
                  color: "#64748b",
                  bg: "#f1f5f9",
                };
                return (
                  <li
                    key={a.id}
                    className="d-flex gap-2"
                    style={{
                      padding: "8px 10px",
                      borderRadius: 10,
                      background: "#f8fafc",
                    }}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: 32, height: 32, flexShrink: 0, background: actionMeta.bg }}
                    >
                      <i className={actionMeta.icon} style={{ color: actionMeta.color, fontSize: 14 }} />
                    </div>
                    <div className="flex-grow-1 min-width-0">
                      <div style={{ fontSize: 13 }}>
                        <strong style={{ color: "#1e293b" }}>
                          {a.actor_snapshot?.name || `User #${a.actor_id}`}
                        </strong>{" "}
                        <span className="text-muted">{a.action.replace(/_/g, " ")}</span>{" "}
                        <span style={{ color: "#475569", fontWeight: 500 }}>
                          {a.entity_type.toLowerCase()}
                        </span>
                      </div>
                      <Tooltip title={new Date(a.created_at).toLocaleString()}>
                        <span className="text-muted" style={{ fontSize: 11.5 }}>
                          {humanReadableDate(a.created_at)}
                        </span>
                      </Tooltip>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {modalLoading && (
            <div className="text-center py-3">
              <Spin />
            </div>
          )}
          {!modalLoading && modalHasMore && modalActivities.length > 0 && (
            <div className="text-center mt-2 mb-2">
              <Button
                type="dashed"
                onClick={() => loadModalActivities(modalPage + 1, true)}
                style={{ borderRadius: 8, color: "#475569" }}
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

/* ─── Info chip helper ─── */
interface InfoChipProps {
  icon: string;
  label: string;
  value: string;
  bg: string;
  color: string;
}

const InfoChip: React.FC<InfoChipProps> = ({ icon, label, value, bg, color }) => (
  <Tooltip title={`${label}: ${value}`}>
    <div
      style={{
        padding: "8px 12px",
        borderRadius: 10,
        background: bg,
        cursor: "help",
        transition: "transform 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
        <i className={icon} style={{ fontSize: 13 }} />
        {label}
      </div>
      <div style={{ fontWeight: 600, fontSize: 13.5, color, marginTop: 1 }}>{value}</div>
    </div>
  </Tooltip>
);

export default ProjectOverview;
